import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { HeartIcon, PauseIcon, PlayIcon, SkipForwardIcon } from 'lucide-react';
import { HeaderStage } from '../components/HeaderStage';
import { Waveform } from '../components/Waveform';
import { Visualizer, type VisualizerStyle } from '../components/Visualizer';
import { usePlayer } from '../contexts/PlayerContext';
import { useLibrary } from '../contexts/LibraryContext';
import { useLyrics } from '../hooks/useLyrics';
import { usePageMeta } from '../hooks/usePageMeta';
import { formatTime } from '../utils/format';
interface NowPlayingProps {
  visualizerStyle: VisualizerStyle;
}
const EASE = [0.23, 1, 0.32, 1] as const;
export function NowPlaying({
  visualizerStyle
}: NowPlayingProps) {
  const {
    currentTrack,
    isPlaying,
    progress,
    duration,
    toggle,
    next
  } = usePlayer();
  const {
    isFavorite,
    toggleFavorite
  } = useLibrary();
  const {
    status,
    lines
  } = useLyrics(currentTrack);

  // Memoised so the head isn't rewritten on every progress tick.
  const recordingSchema = useMemo(() => currentTrack ? {
    '@type': 'MusicRecording',
    name: currentTrack.title,
    byArtist: {
      '@type': 'MusicGroup',
      name: currentTrack.artist
    },
    inAlbum: {
      '@type': 'MusicAlbum',
      name: currentTrack.album
    },
    genre: currentTrack.genre || undefined,
    image: currentTrack.artwork
  } : undefined, [currentTrack]);
  usePageMeta({
    title: currentTrack ? `${currentTrack.title} — ${currentTrack.artist}` : 'Now Playing',
    description: currentTrack ? `Now playing on Noctuzan: ${currentTrack.title} by ${currentTrack.artist}, off ${currentTrack.album}. Live waveform off the real audio, with lyrics where we can find them.` : 'Cover art, a live waveform off the real audio, and lyrics for whatever is spinning on Noctuzan.',
    image: currentTrack?.artwork,
    path: '/now-playing',
    structuredData: recordingSchema
  });
  if (!currentTrack) {
    return <div className="relative px-5 pb-16 pt-8 sm:px-8 lg:px-12" data-id="element-344" data-mp="ohvmu24tjubo">
        <HeaderStage data-id="element-345" data-mp="jmg877yzlue7" />
        <div className="relative z-10 max-w-md" data-id="element-346" data-mp="jj2703227r8c">
          <h1 className="font-display text-4xl text-cream" data-id="element-347" data-mp="el94bjzytfct">Nothing on the turntable</h1>
          <p className="mt-3 text-sm leading-relaxed text-muted" data-id="element-348" data-mp="9ngz5oab7wqo">
            Start a track and this room fills in — cover art, a live wave off the actual audio, and the words where
            we can find them.
          </p>
          <Link to="/" className="mt-6 inline-block rounded-full bg-gold px-5 py-2.5 text-sm font-medium text-ink-base transition-transform duration-150 ease-night hover:scale-[1.02] focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft focus-visible:ring-offset-2 focus-visible:ring-offset-ink-base" data-id="element-349" data-mp="tm3d6v2vinp">
            Back to tonight&apos;s set
          </Link>
        </div>
      </div>;
  }
  return <div className="relative px-5 pb-16 pt-8 sm:px-8 lg:px-12" data-id="element-350" data-mp="1tj4zhi88zqy2">
      <HeaderStage artwork={currentTrack.artwork} data-id="element-351" data-mp="1tiliq4uc38lm" />

      <div className="relative z-10 grid gap-10 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)] lg:gap-14" data-id="element-352" data-mp="1xk6zi19zo713">
        {/* The record */}
        <motion.section aria-label="Now playing" initial={{
        opacity: 0,
        y: 14
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease: EASE
      }} data-id="element-353" data-mp="22l4g49fcot0h">
          <div className="relative overflow-hidden rounded-xl ring-1 ring-cream/10" data-id="element-354" data-mp="19wqhbrgftsnv">
            <img src={currentTrack.artwork} alt={`${currentTrack.album} cover art`} className="aspect-square w-full object-cover" data-id="element-355" data-mp="1ev0do5dwezb2" />
            <div className="absolute -bottom-6 -right-6 h-28 w-28" data-id="element-356" data-mp="1okbhpdlxhds3">
              <Visualizer style={visualizerStyle} className="h-full w-full" data-id="element-357" data-mp="r4nwe3wrjq7t" />
            </div>
          </div>

          <p className="mt-6 text-xs text-muted" data-id="element-358" data-mp="w3h93u487d6x">{currentTrack.genre || 'Uncategorised'}</p>
          <h1 className="mt-1 font-display text-4xl leading-tight text-cream" data-id="element-359" data-mp="2xsn6mra4krac">{currentTrack.title}</h1>
          <p className="mt-2 text-sm text-muted" data-id="element-360" data-mp="2trl6m88dwb7b">
            {currentTrack.artist} · {currentTrack.album}
          </p>

          {/* Live wave off the real audio */}
          <div className="mt-6 h-16 rounded-lg border border-ink-line bg-ink-panel/50 px-3 py-2" data-id="element-361" data-mp="37ksym10dn8pj">
            <Waveform data-id="element-362" data-mp="32pwtq0a1kqv0" />
          </div>
          <p className="mt-2 text-[11px] tabular-nums text-muted" data-id="element-363" data-mp="3hge3c6wtyn2z">
            {formatTime(progress)} / {formatTime(duration)} preview
          </p>

          <div className="mt-6 flex items-center gap-3" data-id="element-364" data-mp="3chjeooc6m4ay">
            <button type="button" onClick={toggle} aria-label={isPlaying ? 'Pause' : 'Play'} className="flex h-12 w-12 items-center justify-center rounded-full bg-gold text-ink-base transition-transform duration-150 ease-night hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft focus-visible:ring-offset-2 focus-visible:ring-offset-ink-base" data-id="element-365" data-mp="3qhu5jrdhriw4">
              {isPlaying ? <PauseIcon className="h-5 w-5" aria-hidden="true" data-id="element-366" data-mp="bl8vzlvr6qmj" /> : <PlayIcon className="h-5 w-5 translate-x-[1px]" aria-hidden="true" data-id="element-367" data-mp="3tzhsb7jnvoxs" />}
            </button>
            <button type="button" onClick={next} aria-label="Next track" className="rounded-full border border-ink-line p-3 text-muted transition-colors duration-150 ease-night hover:border-gold/60 hover:text-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft" data-id="element-368" data-mp="1v76cpnr505i">
              <SkipForwardIcon className="h-4 w-4" aria-hidden="true" data-id="element-369" data-mp="3b2qeveqsw41n" />
            </button>
            <button type="button" onClick={() => toggleFavorite(currentTrack)} aria-pressed={isFavorite(currentTrack.id)} aria-label={isFavorite(currentTrack.id) ? 'Remove from saved' : 'Save track'} className="rounded-full border border-ink-line p-3 text-muted transition-colors duration-150 ease-night hover:border-iris-soft/60 hover:text-iris-soft focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft" data-id="element-370" data-mp="3f2iozvvuei94">
              <HeartIcon className={`h-4 w-4 ${isFavorite(currentTrack.id) ? 'fill-iris-soft text-iris-soft' : ''}`} aria-hidden="true" data-id="element-371" data-mp="2d98lwktjmnm8" />
            </button>
          </div>
        </motion.section>

        {/* Lyrics */}
        <motion.section aria-labelledby="lyrics-heading" initial={{
        opacity: 0,
        y: 14
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        delay: 0.06,
        ease: EASE
      }} className="min-w-0" data-id="element-372" data-mp="30xry3bfprkjl">
          <div className="flex items-baseline justify-between gap-4 border-b border-ink-line pb-4" data-id="element-373" data-mp="3omb39i3gvh7g">
            <h2 id="lyrics-heading" className="font-display text-3xl text-cream" data-id="element-374" data-mp="3jvkvjy7kj29l">
              Lyrics
            </h2>
            <p className="text-xs text-muted" data-id="element-375" data-mp="3r7iucsmdfkm3">
              {status === 'found' ? 'from the open lyrics archive' : 'no time-sync — read at your own pace'}
            </p>
          </div>

          <div aria-live="polite" className="mt-6" data-id="element-376" data-mp="4zrdosgb3nl0">
            {status === 'loading' && <ul className="space-y-3" data-id="element-377" data-mp="90urzaiphbk4">
                {Array.from({
              length: 10
            }).map((_, i) => <li key={i} className="h-3.5 animate-pulse rounded bg-ink-raised" style={{
              width: `${45 + i * 37 % 45}%`
            }} data-id="element-378" data-mp="e0h6dna9sxui" data-mpi={i} />)}
              </ul>}

            {status === 'found' && <div className="max-h-[52vh] overflow-y-auto scroll-thin pr-4" data-id="element-379" data-mp="sotzmchz42lb">
                {lines.map((line, i) => line.length === 0 ? <span key={`gap-${i}`} className="block h-4" data-id="element-380" data-mp="wom34asifijt" /> : <motion.p key={`${i}-${line.slice(0, 12)}`} initial={{
              opacity: 0,
              y: 4
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.2,
              ease: EASE,
              delay: Math.min(i, 14) * 0.02
            }} className="font-display text-lg leading-relaxed text-cream/90" data-id="element-381" data-mp="11q1mlyf1hh03">
                      {line}
                    </motion.p>)}
              </div>}

            {status === 'missing' && <div className="max-w-md" data-id="element-382" data-mp="zc77xtpcjrj3">
                <p className="font-display text-2xl italic text-gold" data-id="element-383" data-mp="z91j8fkmqrnw">No words for this one</p>
                <p className="mt-3 text-sm leading-relaxed text-muted" data-id="element-384" data-mp="1e2516o24v7f5">
                  Instrumentals, library cuts, and obscure pressings usually come back empty. The wave beside it is
                  the whole story tonight.
                </p>
              </div>}

            {status === 'error' && <p className="rounded-sm border border-ink-line bg-ink-panel px-4 py-3 text-sm text-cream" data-id="element-385" data-mp="2s803kzf7etoa">
                The lyrics archive didn&apos;t answer. Skip to the next record or try again in a moment.
              </p>}
          </div>
        </motion.section>
      </div>
    </div>;
}